import React, { useState, useEffect } from 'react';
import { FiAlertCircle, FiPhone, FiEdit, FiCheckCircle, FiDollarSign, FiClock } from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function InspectJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}?action=get_all_jobs`);
      const data = await res.json();
      // ดึงเฉพาะงานที่ช่างรายงานเสนอเพิ่ม (isReportPending == 1)
      setJobs(data.data.filter(j => j.isReportPending == 1));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (job) => {
    const { value: formValues } = await Swal.fire({
      title: '✏️ แก้ไขงาน & อนุมัติราคา',
      html: `
        <label class="block text-left text-sm font-bold mb-1">รายละเอียดงานใหม่</label>
        <textarea id="swal-prob" class="swal2-textarea" style="margin-top:0;">${job.problem}</textarea>
        <label class="block text-left text-sm font-bold mb-1">ราคาที่อนุมัติ (บาท)</label>
        <input id="swal-price" type="number" class="swal2-input" value="${job.price}">
      `,
      showCancelButton: true,
      confirmButtonText: 'บันทึกและอนุมัติ',
      preConfirm: () => ({ problem: document.getElementById('swal-prob').value, price: document.getElementById('swal-price').value })
    });

    if (formValues) {
      Swal.fire({ title: 'กำลังบันทึก...', didOpen: () => Swal.showLoading() });
      const res = await fetch(`${API_URL}?action=update_job`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ id: job.id, problem: formValues.problem, price: formValues.price, isReportPending: 0 })
      });
      if ((await res.json()).success) {
        Swal.fire('สำเร็จ!', 'อัปเดตราคาและอนุมัติเรียบร้อย', 'success');
        fetchJobs();
      }
    }
  };

  const getJobTypeLabel = (type) => {
    const types = { 'aircon': 'แอร์', 'electric': 'ไฟฟ้า', 'contractor': 'รับเหมา', 'solar': 'โซล่าเซลล์', 'door': 'ประตู', 'evertor': 'ลิฟท์', 'camera': 'กล้อง', 'rework': 'งานแก้' };
    return types[type] || 'ทั่วไป';
  };

  return (
    <div className="space-y-6 pb-10 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black text-gray-800 dark:text-white">🔍 ตรวจสอบและประเมินหน้างาน</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">อนุมัติงานที่ช่างรายงานเข้ามา (รอปรับราคา/รายละเอียด)</p>
      </div>

      {isLoading ? (
        <div className="text-center py-20 font-bold text-blue-500">กำลังดึงข้อมูลงานที่ต้องตรวจสอบ...</div>
      ) : jobs.length === 0 ? (
        <div className="bg-white dark:bg-[#1e293b] p-10 rounded-2xl text-center border border-gray-200 dark:border-[#334155] shadow-sm text-gray-400">
          <p className="text-4xl mb-4">✅</p>
          <p className="font-bold">ไม่มีรายการที่ต้องประเมินในขณะนี้</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map(job => (
            <div key={job.id} className="bg-white dark:bg-[#1e293b] p-5 rounded-2xl shadow-sm border border-red-200 dark:border-red-900/30 flex flex-col justify-between min-h-[380px] relative text-left">
              <span className="absolute top-3 right-3 bg-red-100 text-red-700 text-[10px] font-bold px-2 py-1 rounded-full animate-pulse">🚨 รออนุมัติ</span>
              
              <div className="space-y-3 flex-grow">
                {/* ข้อมูลลูกค้า */}
                <div>
                  <p className="text-[9px] uppercase font-bold text-gray-400">ลูกค้าผู้ใช้บริการ</p>
                  <h3 className="font-black text-gray-900 dark:text-white text-base leading-tight mt-0.5">{job.customerName}</h3>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[10px] bg-gray-150 dark:bg-gray-800 px-2 py-0.5 rounded font-bold text-gray-600 dark:text-gray-300">
                      {getJobTypeLabel(job.job_type)}
                    </span>
                    <span className="text-[11px] font-black text-indigo-650 dark:text-indigo-400 select-all font-mono bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 rounded border border-indigo-100/50 dark:border-indigo-900/40">
                      {job.phone || '-'}
                    </span>
                  </div>
                </div>

                {/* รายละเอียดสถานที่ & คิวเวลานัด */}
                <div className="text-xs space-y-1 text-gray-600 dark:text-gray-400">
                  <p><span className="font-bold text-gray-400">📍 ที่อยู่:</span> {job.address || '-'}</p>
                  <p><span className="font-bold text-gray-400">📅 วันนัดหมาย:</span> {job.appointmentDate ? `${new Date(job.appointmentDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })}` : '-'} ({job.appointmentTime === '09:00' ? 'ช่วงเช้า' : job.appointmentTime === '13:00' ? 'ช่วงบ่าย' : job.appointmentTime === '16:30' ? 'ช่วงเย็น' : job.appointmentTime})</p>
                  <p><span className="font-bold text-gray-400">👨‍🔧 ช่างเข้าประเมิน:</span> <span className="font-bold text-gray-800 dark:text-gray-200">{job.technicianName || '-'}</span></p>
                </div>

                {/* รายละเอียดปัญหาแรกเริ่ม */}
                <div className="bg-gray-50 dark:bg-[#0f172a] p-2.5 rounded-lg border border-gray-100 dark:border-slate-800 text-xs">
                  <span className="block text-[9px] font-bold text-gray-400 uppercase mb-0.5">📝 รายละเอียดงานเริ่มแรก:</span>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">{job.problem || '-'}</p>
                </div>

                {/* รายงานที่ช่างรายงานเข้ามา */}
                <div className="bg-red-50 dark:bg-red-900/10 p-3 rounded-xl border border-red-100 dark:border-red-900/30">
                  <span className="block text-[9px] font-bold text-red-500 uppercase mb-1">📢 สิ่งที่ช่างรายงานหน้างาน:</span>
                  <p className="text-xs text-gray-800 dark:text-gray-200 font-bold leading-relaxed">{job.fieldReport || 'ไม่ได้ระบุรายละเอียด'}</p>
                </div>
              </div>

              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-slate-850">
                <a href={`tel:${job.phone}`} className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition">
                  <FiPhone size={14} /> โทรออก
                </a>
                <button onClick={() => handleEdit(job)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition shadow-sm">
                  <FiCheckCircle size={14} /> อนุมัติราคา / แก้ไข
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}